import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { DEFAULT_TOAST_TIMEOUT } from "shared/constants/app-constants"
import { generateUUID } from "shared/lib/generateUUID"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { ToastType } from "shared/types/Toast"

interface UseRequestToastsArgs {
  messages?: {
    loading?: string
    success?: string
    failure?: string
  }
}

interface RaiseToastOptions {
  suppress?: boolean
  timeout?: number | null
}

export function useRequestToasts(args?: UseRequestToastsArgs) {
  const { t } = useTranslation()

  function raiseSuccessToast(
    toastID: number | string,
    options?: RaiseToastOptions
  ) {
    if (!toastID || options?.suppress) return

    toast.update(toastID, {
      autoClose:
        options?.timeout !== undefined
          ? options?.timeout
          : DEFAULT_TOAST_TIMEOUT,
      data: {
        type: ToastType.SUCCESS,
        message: args?.messages?.success ?? t("General.Success")
      }
    })
  }

  function raiseErrorToast(
    toastID: number | string,
    options?: RaiseToastOptions
  ) {
    if (!toastID || options?.suppress) return

    toast.update(toastID, {
      autoClose:
        options?.timeout !== undefined
          ? options?.timeout
          : DEFAULT_TOAST_TIMEOUT,
      data: {
        type: ToastType.ERROR,
        message: args?.messages?.failure ?? t("General.Error")
      }
    })
  }

  const raiseLoadingToast = useEvent((options?: { suppress?: boolean }) => {
    const loadingToastID = options?.suppress
      ? generateUUID()
      : raiseToast({
          type: ToastType.LOADING,
          message: args?.messages?.loading ?? t("General.Loading"),
          autoClose: false
        })

    return {
      raiseSuccessToast: () =>
        raiseSuccessToast(loadingToastID, { suppress: options?.suppress }),
      raiseErrorToast: () =>
        raiseErrorToast(loadingToastID, { suppress: options?.suppress })
    }
  })

  return {
    raiseLoadingToast,
    raiseSuccessToast,
    raiseErrorToast
  }
}
